/**
 * Authentication API
 * 
 * Handles user authentication, session management, and profile data.
 */

import { apiPost, apiPatch, ApiError, API_BASE_URL } from '../api';

// Onboarding context (v0.9.5 - Issue #563)
//
// #2692: the 'club' variant carries BOTH `club_id` (the true UUID) and
// `club_slug` (the URL-routable slug). Web routes and native
// `app/(protected)/clubs/[slug]` are slug-keyed, so post-onboarding navigation
// MUST use the slug — routing on the UUID lands on a 404. Mirrors the backend
// `OnboardingContext` in backend/src/types.ts.
export type OnboardingContext =
  | { type: 'club'; club_id: string; club_slug: string; club_name: string }
  | { type: 'event'; event_id: string; event_name: string }
  | { type: 'founder'; club_id?: string; club_name?: string }
  | { type: 'clean' };

export interface Me {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  public_name_mode: string;
  public_display_name: string;
  name: string | null;
  avatar_url: string | null;
  tagline: string | null;
  birthday_day: number | null;
  birthday_month: number | null;
  location: string | null;
  primary_sport: string | null;
  secondary_sports: string[] | null;
  profile_image_url: string | null;
  // user_level removed - see #785
  subscription_level: string | null;
  distance_unit: 'mi' | 'km'; // Issue #1083: User's preferred distance unit
  timezone: string | null; // #1647: IANA tz for virtual-event notifications
  // Phone (#1461 - SMS notifications)
  phone: string | null;
  phone_verified: boolean;
  // Platform admin system (v0.10.0 - Issue #213)
  is_platform_admin: boolean; // Converted from SQLite number (0/1) to boolean
  global_role: string | null; // 'platform_admin' | 'trust_safety' | 'support' | 'auditor' | 'billing' | null
  // Onboarding tracking (v0.9.5 - Issue #563)
  onboarding_completed: boolean;
  onboarding_context?: OnboardingContext; // Optional - only present if onboarding needed
  // Guided tour tracking (#1559)
  tour_completed_at: number | null; // Unix seconds when tour completed/dismissed, null if pending
  // Server-side default-club selection (#1599). Slug of the user's last
  // demonstrably-active club (GET /api/clubs/:slug as a member updates the
  // underlying users.last_active_club_id). LEFT JOIN-resolved at /auth/me;
  // null when no signal yet or the club has been deleted (ON DELETE SET NULL).
  // usePrimaryClub validates this against useClubs before trusting the value.
  last_active_club_slug: string | null;
  // Add-email recovery hint (#2930 S3a). Present ONLY when a real tier applies
  // (absent === 'none'); computed per-viewer at /auth/me from the user's
  // SMS-suppression state + synthetic-email status. Drives the in-app recovery
  // nudge. Mirror of the backend `RecoveryStatus` union (backend/src/types.ts —
  // keep the two in sync).
  recovery_status?: RecoveryStatus;
  created_at: string;
}

/**
 * Add-email recovery tier (#2930 S3a). `tier1_locked` = phone-only account
 * (synthetic `@sms.oro.fit` email) that replied STOP — locked out when the
 * session lapses. `tier2_inconvenienced` = has a real email, magic-link still
 * works. `none` is an ABSENT field, never sent on the wire.
 */
export type RecoveryStatus = 'tier1_locked' | 'tier2_inconvenienced';

export interface LoginPayload {
  email: string;
  token: string;
}

export interface LoginResponse {
  success: boolean;
}

export interface MagicLinkRequestPayload {
  email: string;
  turnstile_token?: string;
  redirect_url?: string; // Issue #830: Cross-platform redirect
}

// Wire shape is the backend SuccessResponse `{ ok: true }` (auth.ts magic-link
// request handler; generic to prevent user enumeration) — NOT `{ success }`.
// Type corrected in #2326's Rule 55 audit (Frank round 1); callers await void.
export interface MagicLinkRequestResponse {
  ok: true;
  // #2345: set only for the armed App Review demo account — the client shows a
  // static-code entry step instead of "check your email" (the reviewer can't
  // read our inbox). Absent for all normal users.
  reviewer_code_entry?: boolean;
}

// Issue #1328: SMS login support
export interface SmsCodeRequestPayload {
  phone: string;
  turnstile_token?: string;
  invite_code?: string;
  redirect_url?: string;
}

// Same wire shape as magic-link request: backend returns `{ ok: true }`
// (auth.ts SMS request handler). Corrected alongside MagicLinkRequestResponse.
export interface SmsCodeRequestResponse {
  ok: true;
}

export interface MagicLinkConsumePayload {
  token: string;
}

export interface MagicLinkConsumeResponse {
  user: Me;
  redirect_url?: string; // Issue #830: Cross-platform redirect
}

export const authApi = {
  /**
   * Get current user profile
   * GET /auth/me
   * 
   * SPECIAL CASE: 401/403 on /auth/me is a VALID, EXPECTED state for guests.
   * Do NOT convert it to an ApiError here — that breaks public pages.
   * Returns null instead of throwing when unauthenticated.
   */
  me: async (): Promise<Me | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      // Guest user – totally valid on public pages
      if (response.status === 401 || response.status === 403) {
        return null;
      }

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new ApiError(
          body.error?.message || 'Failed to fetch user profile',
          body.error?.code || 'UNKNOWN_ERROR',
          response.status,
          body.error?.details
        );
      }

      // Backend wraps user in { user: Me } envelope
      const payload = await response.json();
      return payload.user ?? payload ?? null;
    } catch (err) {
      if (err instanceof ApiError) throw err; // Let real API errors bubble
      // Network error, JSON parse error, etc. → treat as unauthenticated
      return null;
    }
  },

  /**
   * Login with magic link token
   * POST /auth/login
   */
  login: (payload: LoginPayload) => apiPost<LoginResponse>('/auth/login', payload),

  /**
   * Logout current session
   * POST /auth/logout
   */
  logout: () => apiPost<void>('/auth/logout'),

  /**
   * Request magic link email
   * POST /auth/magic-link/request
   * @param inviteCode - Invite code for invite-first registration (Issue #544)
   * @param redirectUrl - Issue #830: Where to redirect after auth (cross-platform compat)
   */
  requestMagicLink: (email: string, turnstileToken?: string, inviteCode?: string, redirectUrl?: string, termsAccepted?: boolean) =>
    apiPost<MagicLinkRequestResponse>('/auth/magic-link/request', {
      email,
      ...(turnstileToken && { turnstile_token: turnstileToken }),
      ...(inviteCode && { invite_code: inviteCode }),
      ...(redirectUrl && { redirect_url: redirectUrl }),
      // #3312: only sent when the login-screen Terms box was actually ticked.
      // Recorded server-side at /magic-link/consume, never at request time.
      ...(termsAccepted && { terms_accepted: true }),
      // #1647: timezone is sent on /magic-link/consume (auth-proven).
    }),

  /**
   * Request SMS verification code
   * POST /auth/sms/request
   * Issue #1328: SMS login — the backend stores a one-time code and sends it
   * via sendSmsWithConsent; the code is verified at POST /auth/sms/verify.
   */
  requestSmsCode: (phone: string, turnstileToken?: string, inviteCode?: string, redirectUrl?: string, termsAccepted?: boolean) =>
    apiPost<SmsCodeRequestResponse>('/auth/sms/request', {
      phone,
      ...(turnstileToken && { turnstile_token: turnstileToken }),
      ...(inviteCode && { invite_code: inviteCode }),
      ...(redirectUrl && { redirect_url: redirectUrl }),
      // #3312: recorded server-side at /sms/verify once the code is proven.
      ...(termsAccepted && { terms_accepted: true }),
    }),

  /**
   * Consume magic link token and establish session
   * POST /auth/magic-link/consume
   */
  consumeMagicLink: (token: string) =>
    // #1647: tz captured at consume (auth-proven), not at request.
    apiPost<MagicLinkConsumeResponse>('/auth/magic-link/consume', {
      token,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),

  /**
   * Mark onboarding as complete
   * PATCH /auth/onboarding/complete
   */
  completeOnboarding: () =>
    apiPatch<{ success: boolean }>('/auth/onboarding/complete'),

  /**
   * Mark guided tour as completed or dismissed (#1559)
   * PATCH /auth/tour/complete
   */
  completeTour: () =>
    apiPatch<{ tour_completed_at: number }>('/auth/tour/complete'),

  /**
   * Invalidate all sessions across all devices
   * POST /auth/sessions/invalidate-all
   *
   * Issue #533: Multi-device session invalidation.
   * Increments session_version, invalidating all JWT tokens.
   * Returns a new session cookie for the current device.
   * Rate limited: 1 request per 30 seconds.
   */
  invalidateAllSessions: () =>
    apiPost<{ ok: true }>('/auth/sessions/invalidate-all'),

  // #1335: Email change flow (3-step re-authentication)

  /** Step 1: Send verification to current email */
  requestEmailChange: () =>
    apiPost<{ message: string }>('/auth/email-change/request'),

  /** Step 2: Verify current email token + submit new email */
  verifyEmailChange: (token: string, new_email: string) =>
    apiPost<{ message: string }>('/auth/email-change/verify', { token, new_email }),

  /** Step 3: Confirm new email token → email updated */
  completeEmailChange: (token: string) =>
    apiPost<{ message: string }>('/auth/email-change/complete', { token }),
};
